import React from 'react';
import { useIntl } from 'react-intl';
import { Button } from 'shared/components';
import { AITextareaField } from './AITextareaField';
import { ProposalAIPrimaryButton } from './ProposalAIPrimaryButton';

interface Props {
  index: number;
  prompt: string;
  generated: GenerateHeadingSuggestionPayload | null;
  isGenerating: boolean;
  isGeneratedStale: boolean;
  onPromptChange: (value: string) => void;
  onGenerate: React.MouseEventHandler<HTMLButtonElement>;
  onApply: React.MouseEventHandler<HTMLButtonElement>;
  onCancel: React.MouseEventHandler<HTMLButtonElement>;
}

export const HeadingSegmentAIPanel: React.FC<Props> = ({
  index,
  prompt,
  generated,
  isGenerating,
  isGeneratedStale,
  onPromptChange,
  onGenerate,
  onApply,
  onCancel,
}) => {
  const { formatMessage } = useIntl();

  return (
    <div className="proposal-heading-ai__panel">
      <div className="proposal-heading-ai__header">
        <p className="t-label m-bottom--tny">
          {formatMessage({ id: 'proposal-ai.heading.eyebrow' })}
        </p>
        <p className="t-text-2 m-bottom-0">
          {formatMessage({ id: 'proposal-ai.heading.description' })}
        </p>
      </div>

      <AITextareaField
        id={`proposal-ai-heading-prompt-${index}`}
        label={formatMessage({ id: 'proposal-ai.heading.prompt.label' })}
        placeholder={formatMessage({
          id: 'proposal-ai.heading.prompt.placeholder',
        })}
        value={prompt}
        onChange={onPromptChange}
        dataCy={`proposal-ai-heading-prompt-${index}`}
        textareaClassName="proposal-heading-ai__prompt"
      />

      <div className="proposal-ai-generator__action-row">
        <ProposalAIPrimaryButton
          label={
            isGenerating
              ? formatMessage({ id: 'proposal-ai.heading.generate.loading' })
              : generated
                ? formatMessage({
                    id: 'proposal-ai.heading.generate.regenerate',
                  })
                : formatMessage({ id: 'proposal-ai.heading.generate.action' })
          }
          onClick={onGenerate}
          disabled={isGenerating}
          btnSelector="proposal-ai-heading-generate"
          size="med"
        />
        <Button
          style="ghost"
          size="med"
          onClick={onCancel}
          btnSelector="proposal-ai-heading-cancel"
        >
          {formatMessage({ id: 'buttons.cancel' })}
        </Button>
      </div>

      {generated && (
        <>
          {isGeneratedStale && (
            <div className="proposal-ai-generator__notice proposal-ai-generator__notice--warning">
              <p className="t-text-2 m-bottom-0">
                {formatMessage({ id: 'proposal-ai.heading.preview.stale' })}
              </p>
            </div>
          )}

          <div className="proposal-heading-ai__preview">
            <p className="t-label m-bottom--tny">
              {formatMessage({ id: 'proposal-ai.heading.preview.title' })}
            </p>
            <h3 className="proposal-heading-ai__preview-title">
              {generated.title}
            </h3>
            {!!generated.subtitle && (
              <p className="proposal-heading-ai__preview-subtitle t-text-2 m-bottom-0">
                {generated.subtitle}
              </p>
            )}
          </div>

          <div className="proposal-ai-generator__action-row">
            <ProposalAIPrimaryButton
              label={formatMessage({ id: 'proposal-ai.heading.apply' })}
              onClick={onApply}
              disabled={isGeneratedStale}
              btnSelector="proposal-ai-heading-apply"
              size="med"
            />
          </div>
        </>
      )}
    </div>
  );
};
